import { createContext, useContext, useState, useEffect } from "react";
import { useARC } from "./ARC/ARCProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import { isGimbalCameraHealthy } from "lib/video/cameraHealth";

const FeaturesContext = createContext(null);

export const useFeatures = () => {
    const context = useContext(FeaturesContext);
    if (!context) {
        throw new Error("useFeatures must be used within FeaturesProvider");
    }
    return context;
};

/**
 * FeaturesProvider - Tracks availability of optional robot features
 *
 * Subscribes to health topics to detect available features:
 * - /camera/health: Gimbal video via video_pipeline JSON (`gimbal` starts with "running")
 * - /gimbal/health/control: Gimbal control availability
 * - /arm/health: Panthera arm — available only when status is exactly "ok"
 *
 * Gimbal control is marked available if health is not "initializing" or empty.
 */
const FeaturesProvider = ({ children }) => {
    const { controlSocket, connectionState } = useARC();

    const [features, setFeatures] = useState({
        hasGimbalVideo: false,
        hasGimbalControl: false,
        hasArm: false,
    });

    const healthString = (data) => {
        if (data == null) return "";
        if (typeof data === "string") return data;
        if (typeof data?.data === "string") return data.data;
        return "";
    };

    // Gimbal video: video_pipeline /camera/health (not legacy /gimbal/health/video).
    useEffect(() => {
        if (controlSocket && connectionState === ConnectionState.Connected) {
            const handleCameraHealth = (data) => {
                setFeatures((prev) => ({
                    ...prev,
                    hasGimbalVideo: isGimbalCameraHealthy(data),
                }));
            };

            const cleanup = attachRosTopicWithRetry(
                controlSocket,
                "/camera/health",
                handleCameraHealth,
                {
                    ...LATE_ROS_TOPIC_OPTIONS,
                    markMessageReceived: (data) => data != null && data !== "",
                }
            );

            return cleanup;
        } else {
            setFeatures((prev) => ({ ...prev, hasGimbalVideo: false }));
        }
    }, [controlSocket, connectionState]);

    // Subscribe to gimbal control health topic
    useEffect(() => {
        if (controlSocket && connectionState === ConnectionState.Connected) {
            const handleControlHealth = (data) => {
                const status = healthString(data);
                const isAvailable = Boolean(status && status !== "initializing");
                setFeatures((prev) => ({
                    ...prev,
                    hasGimbalControl: isAvailable,
                }));
            };

            const cleanup = attachRosTopicWithRetry(
                controlSocket,
                "/gimbal/health/control",
                handleControlHealth,
                {
                    ...LATE_ROS_TOPIC_OPTIONS,
                    markMessageReceived: (data) => data != null && data !== "",
                }
            );

            return cleanup;
        } else {
            // Mark unavailable when disconnected
            setFeatures((prev) => ({ ...prev, hasGimbalControl: false }));
        }
    }, [controlSocket, connectionState]);

    // Subscribe to arm health topic
    useEffect(() => {
        if (controlSocket && connectionState === ConnectionState.Connected) {
            const handleArmHealth = (data) => {
                const status = healthString(data);
                // Bridge publishes ok | degraded | unreachable | initializing.
                // Only "ok" means HTTP + Socket.IO to the arm server are both up.
                const isAvailable = status === "ok";
                setFeatures((prev) => ({
                    ...prev,
                    hasArm: isAvailable,
                }));
            };

            const cleanup = attachRosTopicWithRetry(
                controlSocket,
                "/arm/health",
                handleArmHealth,
                {
                    ...LATE_ROS_TOPIC_OPTIONS,
                    markMessageReceived: (data) => data != null && data !== "",
                }
            );

            return cleanup;
        } else {
            setFeatures((prev) => ({ ...prev, hasArm: false }));
        }
    }, [controlSocket, connectionState]);

    return (
        <FeaturesContext.Provider value={features}>
            {children}
        </FeaturesContext.Provider>
    );
};

export default FeaturesProvider;
